import { observer } from 'src/utils/mobx-react';
import * as React from 'react';
import { CacheProvider, EmotionCache } from '@emotion/react';
import createCache from '@emotion/cache';
import { MainView } from './MainView';
import { AppState, useAppStateContext } from 'src/appState/AppState';
import { RouteViewType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { Common, ProviderCommon, useCommon } from 'src/domains/common/Common';
import { createRoot } from 'react-dom/client';
import { useState } from 'react';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { DebugPanelState } from 'src/domains/layouts/webview/components/debugPanel/DebugPanelState';

class DebugPanelPortalContentState {
    public readonly myCache: EmotionCache;

    public constructor(root: HTMLElement) {
        this.myCache = createCache({
            key: 'debug-panel',
            container: root,
        });
    }
}

interface DebugPanelPortalContentPropsType {
    appState: AppState;
    cssElement: HTMLElement;
    currentView: RouteViewType;
    common: Common;
}

const DebugPanelPortalContent = observer('DebugPanelPortalContent', (props: DebugPanelPortalContentPropsType) => {
    const [state] = React.useState(() => new DebugPanelPortalContentState(props.cssElement));

    return (
        <ProviderCommon value={props.common}>
            <CacheProvider value={state.myCache}>
                <MainView
                    appState={props.appState}
                    currentView={props.currentView}
                />
            </CacheProvider>
        </ProviderCommon>
    );
});

interface SizeType {
    width: number;
    height: number;
    left: number;
    top: number;
}

class DebugPanelStateLocal {
    public containerEl = document.createElement('div');
    public root = createRoot(this.containerEl);
}

interface DebugPanePropsType {
    currentView: RouteViewType | null;
}

const DebugPanelPortal = observer('DebugPanelPortal', ({ currentView }: DebugPanePropsType) => {
    const appState = useAppStateContext();
    const common = useCommon();
    const debugPanel = DebugPanelState.get(common);

    const [state] = useState(() => new DebugPanelStateLocal());

    const cssElement = document.createElement('div');
    let externalWindow: Window | null = null;

    let timer: NodeJS.Timer | null = null;

    if (currentView === null) {
        return null;
    }

    const formatParams = (params: Record<string, number | string>): string => {
        const out: Array<string> = [];

        for (const [key, value] of Object.entries(params)) {
            out.push(`${key}=${value}`);
        }
        return out.join(',');
    };

    const getInitSize = (): SizeType => {
        const size = debugPanel.size.getValue();

        if (size === null) {
            return {
                width: 600,
                height: 400,
                left: 200,
                top: 200,
            };
        }

        return size;
    };

    const getInitSizeParams = (): string => {
        const params = getInitSize();
        const paramsRecord: Record<string, string | number> = {
            ...params,
        };
        return formatParams(paramsRecord);
    };

    const unmountWindow = (): void => {
        if (externalWindow !== null) {
            externalWindow.close();
        }
        state.root.unmount();

        window.removeEventListener('beforeunload', unmountWindow);

        if (timer !== null) {
            clearInterval(timer);
            timer = null;
        }
    };

    const createWindow = (): Window | null => {
        const externalWindow = window.open('', '', getInitSizeParams());

        if (externalWindow === null) {
            console.error('DebugPanel - createWindow error');
            return null;
        }

        externalWindow.document.body.appendChild(cssElement);
        externalWindow.document.body.appendChild(state.containerEl);
        externalWindow.document.title = 'Debug panel';

        externalWindow.addEventListener('beforeunload', () => {
            debugPanel.close();
        });

        window.addEventListener('beforeunload', unmountWindow);

        timer = setInterval(() => {
            const size: SizeType = {
                left: externalWindow.screenLeft,
                top: externalWindow.screenTop,
                width: externalWindow.innerWidth,
                height: externalWindow.innerHeight,
            };
            debugPanel.size.setValue(size);
        }, 1000);

        return externalWindow;
    };

    React.useEffect(() => {
        externalWindow = createWindow();

        const domJSX = (
            <DebugPanelPortalContent
                appState={appState}
                currentView={currentView}
                cssElement={cssElement}
                common={common}
            />
        );

        state.root.render(domJSX);

        return (): void => {
            unmountWindow();
        };
    }, []);

    return null;
});

export const DebugPanel = observer('DebugPanel', (props: DebugPanePropsType) => {
    const common = useCommon();
    const debugPanel = DebugPanelState.get(common);
    const env = EnvironmentState.get(common);

    if (props.currentView === null) {
        return null;
    }

    if (env.isBrowser && debugPanel.isVisible) {
        return <DebugPanelPortal currentView={props.currentView} />;
    }

    return null;
});
